import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAuthStore } from '../store/authStore';
import { useTenantStore } from '../store/tenantStore';
import { useQuery } from '@tanstack/react-query';
import { empresaService } from '../services/empresaService';
import Logo from './Logo';

interface NavItem {
  name: string;
  href: string;
  // También activo en subrutas (p. ej. /areas/:id/tareas)
  prefijo?: boolean;
  // La API de la página exige rol Admin; para otros roles respondería 403
  soloAdmin?: boolean;
}

// Lo que se usa todos los días va a la vista; la configuración, agrupada aparte.
const OPERACION: NavItem[] = [
  { name: 'Dashboard', href: '/app' },
  { name: 'Solicitudes', href: '/solicitudes' },
  { name: 'Registros', href: '/registros' },
  { name: 'Reportes', href: '/reportes' },
];

const CONFIGURACION: NavItem[] = [
  { name: 'Pisos', href: '/pisos' },
  { name: 'Áreas', href: '/areas', prefijo: true },
  { name: 'Tipos de áreas', href: '/tipo-areas' },
  { name: 'Tareas', href: '/tareas' },
  { name: 'Enlaces QR', href: '/qr-links' },
  // /auth/usuarios y /Empresas/miembros son [Authorize(Roles = "Admin")] / "Admin,SuperAdmin"
  { name: 'Usuarios', href: '/usuarios', soloAdmin: true },
  // WhatsAppController: todas sus acciones son [Authorize(Roles = "Admin")]
  { name: 'WhatsApp', href: '/whatsapp-config', soloAdmin: true },
];

const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const Navigation: React.FC = () => {
  const { logout, currentUser, isLogoutLoading } = useAuth();
  const { user } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const configRef = useRef<HTMLDivElement>(null);
  const { empresaNombre, clearTenant } = useTenantStore();
  const { data: misEmpresas } = useQuery({
    queryKey: ['misEmpresasNav'],
    queryFn: empresaService.getMisEmpresas,
  });
  const canChangeEmpresa = (misEmpresas?.length || 0) > 1;

  const rol = currentUser?.rol ?? user?.rol;
  const isSuperAdmin = rol === 'SuperAdmin';
  // SuperAdmin recibe también el rol Admin en el token (UsuarioService.GenerateJwtToken)
  const isAdmin = rol === 'Admin' || isSuperAdmin;
  const visible = (item: NavItem) => !item.soloAdmin || isAdmin;

  const operacion = isSuperAdmin
    ? [{ name: 'Super Admin', href: '/super-admin', prefijo: true }, ...OPERACION]
    : OPERACION;
  const configuracion = CONFIGURACION.filter(visible);

  const isActive = (item: NavItem) =>
    location.pathname === item.href || (!!item.prefijo && location.pathname.startsWith(`${item.href}/`));
  const configActiva = configuracion.some(isActive);
  const paginaActual = [...operacion, ...configuracion].find(isActive);

  // Cerrar menús al navegar
  useEffect(() => {
    setUserMenuOpen(false);
    setConfigOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (userMenuRef.current && !userMenuRef.current.contains(target)) setUserMenuOpen(false);
      if (configRef.current && !configRef.current.contains(target)) setConfigOpen(false);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setUserMenuOpen(false);
        setConfigOpen(false);
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const cambiarEmpresa = () => {
    setUserMenuOpen(false);
    setMobileOpen(false);
    clearTenant();
    navigate('/select-empresa');
  };

  const cerrarSesion = () => {
    setUserMenuOpen(false);
    setMobileOpen(false);
    logout();
  };

  const linkDesktop = (active: boolean) =>
    `px-3 py-2 rounded-md text-sm font-medium ${
      active ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
    }`;

  const linkMovil = (active: boolean) =>
    `block px-3 py-3 rounded-md text-base font-medium ${
      active ? 'bg-indigo-50 text-indigo-700' : 'text-gray-800 hover:bg-gray-100'
    }`;

  return (
    <nav className="bg-white shadow relative z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <div className="flex items-center gap-6 min-w-0">
            <div className="flex-shrink-0">
              <Logo size="md" showTagline={false} />
            </div>

            {/* Escritorio: operación a la vista, configuración en un desplegable */}
            <div className="hidden lg:flex items-center gap-1">
              {operacion.map((item) => (
                <Link key={item.href} to={item.href} className={linkDesktop(isActive(item))} aria-current={isActive(item) ? 'page' : undefined}>
                  {item.name}
                </Link>
              ))}
              {configuracion.length > 0 && (
                <div className="relative" ref={configRef}>
                  <button
                    type="button"
                    onClick={() => setConfigOpen((o) => !o)}
                    aria-expanded={configOpen}
                    aria-haspopup="true"
                    className={`${linkDesktop(configActiva)} inline-flex items-center gap-1`}
                  >
                    Configuración
                    <Chevron open={configOpen} />
                  </button>
                  {configOpen && (
                    <div className="absolute left-0 mt-2 w-56 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 py-1">
                      {configuracion.map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          aria-current={isActive(item) ? 'page' : undefined}
                          className={`block px-4 py-2 text-sm ${
                            isActive(item) ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Móvil: dónde estoy */}
            {paginaActual && (
              <span className="lg:hidden truncate text-base font-semibold text-gray-900">{paginaActual.name}</span>
            )}
          </div>

          {/* Escritorio: empresa y usuario */}
          <div className="hidden lg:flex items-center gap-3">
            {empresaNombre && (
              <div className="text-sm text-gray-600 truncate max-w-[14rem]" title={empresaNombre}>
                {empresaNombre}
              </div>
            )}
            <div className="relative" ref={userMenuRef}>
              <button
                type="button"
                onClick={() => setUserMenuOpen((o) => !o)}
                aria-expanded={userMenuOpen}
                aria-haspopup="true"
                className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-md px-2 py-1"
              >
                <span>{currentUser?.nombre || 'Usuario'}</span>
                <Chevron open={userMenuOpen} />
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 py-1">
                  {canChangeEmpresa && (
                    <>
                      <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={cambiarEmpresa}>
                        Cambiar empresa
                      </button>
                      <div className="border-t my-1" />
                    </>
                  )}
                  <button
                    onClick={cerrarSesion}
                    disabled={isLogoutLoading}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    {isLogoutLoading ? 'Cerrando sesión...' : 'Cerrar sesión'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Móvil: botón de menú */}
          <button
            type="button"
            onClick={() => setMobileOpen((o) => !o)}
            aria-expanded={mobileOpen}
            aria-controls="menu-movil"
            aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
            className="lg:hidden -mr-2 p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {mobileOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Móvil: menú desplegado, agrupado y con botones grandes */}
      {mobileOpen && (
        <div id="menu-movil" className="lg:hidden absolute inset-x-0 top-16 bg-white border-t border-gray-200 shadow-lg max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 py-4 space-y-5">
            <div>
              <div className="px-3 mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Operación</div>
              {operacion.map((item) => (
                <Link key={item.href} to={item.href} className={linkMovil(isActive(item))} aria-current={isActive(item) ? 'page' : undefined}>
                  {item.name}
                </Link>
              ))}
            </div>
            {configuracion.length > 0 && (
              <div>
                <div className="px-3 mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Configuración</div>
                {configuracion.map((item) => (
                  <Link key={item.href} to={item.href} className={linkMovil(isActive(item))} aria-current={isActive(item) ? 'page' : undefined}>
                    {item.name}
                  </Link>
                ))}
              </div>
            )}
            <div className="border-t border-gray-200 pt-4">
              <div className="px-3 text-base font-medium text-gray-900">{currentUser?.nombre || 'Usuario'}</div>
              {empresaNombre && <div className="px-3 text-sm text-gray-600">{empresaNombre}</div>}
              <div className="mt-2">
                {canChangeEmpresa && (
                  <button className={`${linkMovil(false)} w-full text-left`} onClick={cambiarEmpresa}>
                    Cambiar empresa
                  </button>
                )}
                <button
                  onClick={cerrarSesion}
                  disabled={isLogoutLoading}
                  className={`${linkMovil(false)} w-full text-left disabled:text-gray-400`}
                >
                  {isLogoutLoading ? 'Cerrando sesión...' : 'Cerrar sesión'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
